import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { Logo } from "@/components/Logo";
import { ChessBoard } from "@/chess/board/ChessBoard";
import {
  coordsFromSquare,
  fenToBoard,
  sideToMove,
  uciSquares,
  uciToSan,
  useSwipeDrill,
  useCompleteSession,
  useMarkItemSolved,
  visualCardsLeft,
  offsetShadow,
  TYPE,
  type SwipeDirection,
  type TrainingSessionOut,
} from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

interface Props {
  session: TrainingSessionOut;
}

// How long the card's throw-off animation runs at the end of feedback.
const THROW_MS = 250;

/**
 * Tinder-style swipe drill. One card at a time on top of a visible stack:
 * judge cards show a single candidate move (right = "best", left = "not
 * best"), duel cards show two color-coded moves (swipe toward the better).
 * ArrowLeft/ArrowRight and two large buttons under the board both swipe; the
 * card throws off-screen during the feedback flash, then the next card loads.
 */
export function SwipeDrillPlay({ session }: Props) {
  const p = useTheme();
  const { isMobile, width } = useViewport();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const fromGameId = search.get("from");
  const doneDestination = fromGameId ? `/app/analysis/${fromGameId}` : "/app";

  const variant = session.swipe_variant ?? "judge";
  const markSolved = useMarkItemSolved();
  const completeSession = useCompleteSession();

  // Local correct/total score for the completion screen.
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const drill = useSwipeDrill({
    items: session.items,
    variant,
    onSolveItem: (item, outcome) => {
      setScore((s) => ({ correct: s.correct + (outcome.solved ? 1 : 0), total: s.total + 1 }));
      markSolved.mutate({ itemId: item.id, outcome });
    },
    onComplete: () => {
      if (!fromGameId) completeSession.mutate(session.id);
    },
  });

  // Throw the card once feedback has been visible long enough to read.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (!drill.resolved) {
      setLeaving(false);
      return;
    }
    const id = setTimeout(() => setLeaving(true), Math.max(0, 1100 - THROW_MS));
    return () => clearTimeout(id);
  }, [drill.resolved, drill.index]);

  // Keyboard: arrow keys swipe while a card is live.
  const swipeRef = useRef(drill.swipe);
  swipeRef.current = drill.swipe;
  const phaseRef = useRef(drill.phase);
  phaseRef.current = drill.phase;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat || phaseRef.current !== "swiping") return;
      if (e.key === "ArrowLeft") swipeRef.current("left");
      if (e.key === "ArrowRight") swipeRef.current("right");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const item = drill.current;
  const startFen = item?.position_fen ?? null;
  const playerColor: "w" | "b" = startFen ? sideToMove(startFen) : "w";
  const board = useMemo(
    () => (startFen ? fenToBoard(startFen) : Array.from({ length: 8 }, () => Array(8).fill(null))),
    [startFen],
  );

  // The card's moves with display colors + SAN. Judge = 1 entry (choiceA),
  // duel = 2 entries (left choiceA, right choiceB).
  const options = useMemo(() => {
    if (!item) return [];
    const colors = [p.choiceA, p.choiceB];
    return item.choices.map((c, i) => ({
      choice: c,
      color: colors[i % colors.length],
      san: uciToSan(startFen, c.uci) || c.uci,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, startFen]);

  const bestUci = item?.best_moves?.[0] ?? null;

  // Candidate arrows; after resolve, the best move is drawn green on top.
  const arrows = useMemo(() => {
    const out: { from: { row: number; col: number }; to: { row: number; col: number }; color: string }[] = [];
    options.forEach((o) => {
      const sq = uciSquares(o.choice.uci);
      if (sq) out.push({ from: coordsFromSquare(sq.from), to: coordsFromSquare(sq.to), color: o.color });
    });
    if (drill.resolved && bestUci) {
      const sq = uciSquares(bestUci);
      if (sq) out.push({ from: coordsFromSquare(sq.from), to: coordsFromSquare(sq.to), color: p.success });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, drill.resolved, bestUci]);

  // --- completion screen ---------------------------------------------------
  if (drill.completed) {
    return (
      <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, display: "grid", placeItems: "center", padding: 24, fontFamily: TYPE.body }}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 18, alignItems: "center" }}>
          <BrLabel>SWIPE · DONE</BrLabel>
          <div style={{ fontFamily: TYPE.display, fontSize: 48, fontWeight: 800, letterSpacing: -2, textTransform: "uppercase" }}>
            {score.correct} / {score.total}
          </div>
          <div style={{ fontFamily: TYPE.mono, fontSize: 12, textTransform: "uppercase", color: p.fgMuted }}>
            correct swipes
          </div>
          <BrBtn variant="accent" size="lg" onClick={() => nav(doneDestination)}>
            {fromGameId ? "Back to game →" : "Back to training →"}
          </BrBtn>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, display: "grid", placeItems: "center", padding: 24, fontFamily: TYPE.body, fontWeight: 700, textAlign: "center" }}>
        <div>
          <div style={{ marginBottom: 12 }}>No positions in this session.</div>
          <BrBtn variant="ghost" onClick={() => nav("/app/train")}>← Back</BrBtn>
        </div>
      </div>
    );
  }

  // Mobile allowance: main padding (28) + card padding/border (36) + the
  // stack's diagonal offset (up to 24) so the deck never overflows the viewport.
  const BOARD = Math.min(isMobile ? width - 92 : 420, 420);
  const timeLimit = item.time_limit_seconds ?? null;
  const timeLeft = drill.remainingSec ?? 0;
  const timerDanger = timeLimit != null && timeLeft <= Math.max(2, Math.round(timeLimit * 0.34));
  const visualLeft = visualCardsLeft(drill.index, drill.total, session.swipe_time_based);
  const underlays = Math.min(4, Math.max(0, visualLeft - 1));
  const counter = session.swipe_time_based
    ? `~${visualLeft} LEFT`
    : `CARD ${drill.index + 1} / ${drill.total}`;

  // Throw transform: sideways for a swipe, downward fade on timeout.
  const throwTransform = drill.timedOut
    ? "translateY(60%) rotate(0deg)"
    : `translateX(${drill.swiped === "left" ? "-130%" : "130%"}) rotate(${drill.swiped === "left" ? -14 : 14}deg)`;

  const feedbackBorder = drill.resolved ? (drill.correct ? p.success : p.danger) : p.border;
  const prompt =
    drill.phase === "swiping"
      ? variant === "judge"
        ? "Best move?"
        : "Which is best?"
      : drill.timedOut
        ? "Time's up."
        : drill.correct
          ? "Correct!"
          : "Not quite.";

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body }}>
      {/* Top bar */}
      <div style={{ borderBottom: `2px solid ${p.border}`, padding: isMobile ? "10px 14px" : "12px 24px", display: "flex", alignItems: "center", gap: 14 }}>
        <BrBtn size="xs" variant="surface" withShadow={false} onClick={() => nav(doneDestination)} ariaLabel="Back">←</BrBtn>
        {!isMobile && <Logo size={22} />}
        <div>
          <div style={{ fontSize: 11, fontFamily: TYPE.mono, letterSpacing: 1, textTransform: "uppercase" }}>
            SWIPE · {variant === "judge" ? "JUDGE" : "DUEL"}
          </div>
          <div style={{ fontSize: 10, fontFamily: TYPE.mono, color: p.fgMuted, textTransform: "uppercase" }}>
            {counter}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {timeLimit != null && (
          <div
            style={{
              fontFamily: TYPE.display,
              fontSize: 32,
              fontWeight: 800,
              letterSpacing: -1,
              minWidth: 56,
              textAlign: "right",
              color: drill.phase === "swiping" ? (timerDanger ? p.danger : p.fg) : p.fgFaint,
            }}
          >
            {drill.phase === "swiping" ? `0:${String(Math.max(0, timeLeft)).padStart(2, "0")}` : "—"}
          </div>
        )}
      </div>

      <main style={{ maxWidth: 640, margin: "0 auto", padding: isMobile ? "20px 14px 40px" : "28px 24px 56px", display: "flex", flexDirection: "column", gap: 18, alignItems: "center" }}>
        {/* Card stack */}
        <div style={{ position: "relative", width: BOARD + 36 + underlays * 6, paddingBottom: underlays * 6 }}>
          {/* Underlay cards — the visible remaining stack. */}
          {Array.from({ length: underlays }, (_, i) => {
            const d = (underlays - i) * 6;
            return (
              <div
                key={`under-${i}`}
                style={{
                  position: "absolute",
                  inset: 0,
                  transform: `translate(${d}px, ${d}px)`,
                  background: p.card,
                  border: `2px solid ${p.border}`,
                  zIndex: i,
                }}
              />
            );
          })}

          {/* Top card — keyed by item so ADVANCE remounts with a clean transform. */}
          <div
            key={item.id}
            style={{
              position: "relative",
              zIndex: underlays + 1,
              background: p.card,
              border: `2px solid ${feedbackBorder}`,
              boxShadow: offsetShadow(p, 4, 4),
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              transform: leaving ? throwTransform : "none",
              opacity: leaving ? 0 : 1,
              transition: leaving ? `transform ${THROW_MS}ms ease-in, opacity ${THROW_MS}ms ease-in` : "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
              <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 18 : 22, fontWeight: 800, letterSpacing: -0.6, textTransform: "uppercase", color: drill.resolved ? (drill.correct ? p.success : p.danger) : p.fg }}>
                {prompt}
              </div>
              <div style={{ fontSize: 10, fontFamily: TYPE.mono, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, whiteSpace: "nowrap" }}>
                {playerColor === "w" ? "WHITE TO MOVE" : "BLACK TO MOVE"}
              </div>
            </div>

            {/* Judge: the single candidate as a big SAN chip. Duel: handled by buttons. */}
            {variant === "judge" && options[0] && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 14, height: 14, background: options[0].color, border: `2px solid ${p.border}` }} />
                <span style={{ fontFamily: TYPE.mono, fontSize: 18, fontWeight: 800 }}>{options[0].san}</span>
              </div>
            )}

            <ChessBoard
              palette={p}
              size={BOARD}
              position={board}
              arrows={arrows}
              flipped={playerColor === "b"}
              showCoords
              animMs={0}
            />

            {/* Per-card timer bar */}
            {timeLimit != null && (
              <div style={{ height: 8, border: `2px solid ${p.border}`, background: p.bg }}>
                <div
                  style={{
                    height: "100%",
                    width: `${(Math.max(0, timeLeft) / timeLimit) * 100}%`,
                    background: timerDanger ? p.danger : p.accent,
                    transition: "width 950ms linear",
                  }}
                />
              </div>
            )}

            {/* Best-move reveal during feedback */}
            {drill.resolved && (
              <div style={{ fontFamily: TYPE.mono, fontSize: 13, fontWeight: 700, textTransform: "uppercase" }}>
                {variant === "judge" && (
                  <span style={{ marginRight: 10 }}>
                    {options[0]?.choice.kind === "best" ? "It was the best move." : "It was not the best move."}
                  </span>
                )}
                {bestUci && <span style={{ color: p.success }}>Best: {uciToSan(startFen, bestUci) || bestUci}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Large swipe arrows under the board */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, width: BOARD + 36 }}>
          <SwipeButton
            dir="left"
            label={variant === "judge" ? "NOT BEST" : options[0]?.san ?? "LEFT"}
            color={variant === "judge" ? p.danger : options[0]?.color ?? p.choiceA}
            fg={variant === "judge" ? p.dangerFg : "#ffffff"}
            disabled={drill.phase !== "swiping"}
            onClick={() => drill.swipe("left")}
            p={p}
          />
          <SwipeButton
            dir="right"
            label={variant === "judge" ? "BEST MOVE" : options[1]?.san ?? "RIGHT"}
            color={variant === "judge" ? p.success : options[1]?.color ?? p.choiceB}
            fg={variant === "judge" ? p.successFg : "#ffffff"}
            disabled={drill.phase !== "swiping"}
            onClick={() => drill.swipe("right")}
            p={p}
          />
        </div>
        <div style={{ fontSize: 10, fontFamily: TYPE.mono, letterSpacing: 1, textTransform: "uppercase", color: p.fgFaint }}>
          ← → arrow keys work too
        </div>
      </main>
    </div>
  );
}

function SwipeButton({
  dir,
  label,
  color,
  fg,
  disabled,
  onClick,
  p,
}: {
  dir: SwipeDirection;
  label: string;
  color: string;
  fg: string;
  disabled: boolean;
  onClick: () => void;
  p: ReturnType<typeof useTheme>;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={`Swipe ${dir}: ${label}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "18px 14px",
        background: color,
        color: fg,
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, 4, 4),
        cursor: disabled ? "default" : "pointer",
        fontFamily: TYPE.body,
        fontWeight: 800,
        fontSize: 18,
        textTransform: "uppercase",
        letterSpacing: -0.3,
        opacity: disabled ? 0.55 : 1,
        minHeight: 64,
      }}
    >
      {dir === "left" && <span style={{ fontSize: 26, lineHeight: 1 }}>←</span>}
      <span>{label}</span>
      {dir === "right" && <span style={{ fontSize: 26, lineHeight: 1 }}>→</span>}
    </button>
  );
}
